import React from 'react';
import { formatMonthWithYear } from '../../utils/formatters';
import { useEvaluationSummaryData } from '../../hooks/useEvaluationQueries';
import { EvaluationMetrics } from '../../types/evaluation';

interface PerformanceSummaryProps {
  branchId?: number;
  timeRange?: number;
  onError?: (error: Error) => void;
  showLoading?: boolean;
  compact?: boolean;
  selectedMonth?: string | null;
  selectedMonthData?: EvaluationMetrics | null;
}

const PerformanceSummary: React.FC<PerformanceSummaryProps> = ({
  branchId = 0,
  timeRange,
  onError,
  showLoading = true,
  compact = false,
  selectedMonth,
  selectedMonthData
}) => {
  // Use React Query hook for summary data
  const { 
    data,
    isLoading, 
    isError, 
    error 
  } = useEvaluationSummaryData(branchId, timeRange);
  
  const summaryData = data?.summaryData;

  React.useEffect(() => {
    if (isError && onError && error) {
      onError(error as Error);
    }
  }, [isError, error, onError]);

  if (isLoading && showLoading) {
    return (
      <div className="card-base">
        <div className="p-6">
          <div className="text-text-secondary text-center">در حال بارگذاری…</div>
        </div>
      </div>
    );
  }

  if (selectedMonth && selectedMonthData) {
    // Monthly Details View
    return (
      <>
        <div className="border-b border-border-primary p-6">
          <h3 className="title-card">تحلیل ماهانه</h3>
          <p className="text-text-primary text-center">{formatMonthWithYear(selectedMonth)}</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-status-info-bg rounded-lg p-4 border border-status-info">
              <div className="text-sm text-text-primary mb-1">تعداد سفارش</div>
              <div className="text-2xl font-bold text-status-info">{selectedMonthData.تعداد_فاکتورها?.toLocaleString()}</div>
            </div>
            <div className="bg-status-warning-bg rounded-lg p-4 border border-status-warning">
              <div className="text-sm text-text-primary mb-1">اشتراک‌گیری</div>
              <div className="text-2xl font-bold text-status-warning">{selectedMonthData.اشتراک_گیری}%</div>
            </div>
            <div className="bg-status-success-bg rounded-lg p-4 border border-status-success">
              <div className="text-sm text-text-primary mb-1">درصد نظرات مثبت</div>
              <div className="text-2xl font-bold text-status-success">{selectedMonthData.درصد_نظر_راضی}%</div>
            </div>
            <div className="bg-status-info-bg rounded-lg p-4 border border-status-info">
              <div className="text-sm text-text-primary mb-1">مشارکت در نظرسنجی</div>
              <div className="text-2xl font-bold text-status-info">{selectedMonthData.مشارکت_در_نظرسنجی}%</div>
            </div>
          </div>

          <div className="bg-bg-secondary rounded-lg p-4 mb-6">
            <h3 className="title-card">تحلیل نظرات مشتریان</h3>
            <div className="space-y-4">
              <div className="bg-bg-secondary rounded-lg p-4 border border-border-primary">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-text-secondary">نظرات راضی</span>
                  <span className="text-lg font-bold text-text-primary">{selectedMonthData.تعداد_نظر_راضی?.toLocaleString()}</span>
                </div>
                <div className="w-full bg-bg-secondary rounded-full h-2 mb-2">
                  <div className="bg-status-success h-2 rounded-full transition-all" style={{width: `${selectedMonthData.درصد_نظر_راضی}%`}}></div>
                </div>
                <div className="flex justify-center">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-status-success text-text-inverse">
                    {selectedMonthData.درصد_نظر_راضی}%
                  </span>
                </div>
              </div>

              <div className="bg-bg-secondary rounded-lg p-4 border border-border-primary">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-text-secondary">نظرات ناراضی</span>
                  <span className="text-lg font-bold text-text-primary">{selectedMonthData.تعداد_نظر_ناراضی?.toLocaleString()}</span>
                </div>
                <div className="w-full bg-bg-secondary rounded-full h-2 mb-2">
                  <div className="bg-status-warning h-2 rounded-full transition-all" style={{width: `${selectedMonthData.درصد_نظر_ناراضی}%`}}></div>
                </div>
                <div className="flex justify-center">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-status-warning text-text-inverse">
                    {selectedMonthData.درصد_نظر_ناراضی}%
                  </span>
                </div>
              </div>
              
              <div className="border-t border-border-primary pt-4">
                <div className="grid grid-cols-1 gap-3">
                  <div className="data-row">
                    <span className="text-sm font-medium text-text-primary">مجموع نظرات</span>
                    <span className="text-lg font-bold text-text-primary">{selectedMonthData.تعداد_کل_نظرات?.toLocaleString()}</span>
                  </div>
                  <div className="data-row">
                    <span className="text-sm font-medium text-text-primary">پیامک‌های ارسالی</span>
                    <span className="text-lg font-bold text-text-primary">{selectedMonthData.تعداد_ارسال_پیامکی?.toLocaleString()}</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-status-warning-bg rounded-lg p-4 border border-status-warning text-center">
                <div className="text-sm font-medium text-text-primary mb-1">روزهای بدون شارژ پیامک</div>
                <div className="text-3xl font-bold text-status-warning">{selectedMonthData.تعداد_روز_بدون_شارژ_پیامک}</div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Summary View
  return (
    <>
      <div className="border-b border-border-primary p-6">
        <h3 className="title-card">خلاصه عملکرد</h3>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-status-warning-bg rounded-lg p-4 border border-status-warning">
            <div className="flex items-center mb-3">
              <div className="text-sm font-medium text-text-primary">میانگین اشتراک‌گیری</div>
            </div>
            <div className="text-2xl font-bold text-status-warning">
              {summaryData?.avgSubscription?.toFixed(2) || '0'}%
            </div>
          </div>
          <div className="bg-status-success-bg rounded-lg p-4 border border-status-success">
            <div className="flex items-center mb-3">
              <div className="text-sm font-medium text-text-primary">میانگین نظرات مثبت</div>
            </div>
            <div className="text-2xl font-bold text-status-success">
              {summaryData?.avgSatisfaction?.toFixed(1) || '0'}%
            </div>
          </div>
          <div className="bg-status-info-bg rounded-lg p-4 border border-status-info">
            <div className="flex items-center mb-3">
              <div className="text-sm font-medium text-text-primary">کل سفارش‌ها</div>
            </div>
            <div className="text-2xl font-bold text-status-info">
              {summaryData?.totalInvoices?.toLocaleString() || '0'}
            </div>
          </div>
          <div className="bg-status-info-bg rounded-lg p-4 border border-status-info">
            <div className="flex items-center mb-3">
              <div className="text-sm font-medium text-text-primary">میانگین مشارکت</div>
            </div>
            <div className="text-2xl font-bold text-status-info">
              {summaryData?.avgParticipation?.toFixed(1) || '0'}%
            </div>
          </div>
        </div>

        <div className="bg-status-info-bg border border-status-info rounded-lg p-4 text-center">
          <div className="flex items-center justify-center mb-2">
            <span className="text-2xl mr-2">💡</span>
            <span className="text-sm font-medium text-text-primary">راهنما</span>
          </div>
          <span className="text-sm text-text-primary animate-loading-pulse">
            برای مشاهده جزئیات، روی هر ماه در نقشه حرارتی کلیک کنید
          </span>
        </div>
      </div>
    </>
  );
};

export default PerformanceSummary;